import { Box, Theme } from '@mui/material'
import { alpha, keyframes } from '@mui/material/styles'
import { SystemStyleObject } from '@mui/system'
import { FC, ReactNode } from 'react'
import { border } from '../../layout/theme/border'
import { StatusTone, statusColor } from '../../layout/theme/color'

// Pulse ring in the dot's own colour, e.g. green for ACTIVE.
const pulse = (color: string) => keyframes`
  0% { box-shadow: 0 0 0 0 ${alpha(color, 0.35)}; }
  70% { box-shadow: 0 0 0 5px ${alpha(color, 0)}; }
  100% { box-shadow: 0 0 0 0 ${alpha(color, 0)}; }
`

/** Pill style of a tone, for custom pills that share the look. */
const pillSx = (tone: StatusTone): SystemStyleObject<Theme> => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.75,
  height: 22,
  px: 1,
  borderRadius: border.radius.pill,
  // eslint-disable-next-line security/detect-object-injection -- closed union key
  bgcolor: statusColor[tone].bg,
  // eslint-disable-next-line security/detect-object-injection -- closed union key
  border: `${border.thin} solid ${statusColor[tone].border}`,
  // eslint-disable-next-line security/detect-object-injection -- closed union key
  color: statusColor[tone].fg,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1,
  whiteSpace: 'nowrap',
})

/** Dot in the colour of a tone; `pulse` for "live" states. */
const StatusDot: FC<{ tone: StatusTone; size?: number; pulse?: boolean }> = ({ tone, size = 7, pulse: pulsing }) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      display: 'inline-block',
      width: size,
      height: size,
      borderRadius: '50%',
      flexShrink: 0,
      // eslint-disable-next-line security/detect-object-injection -- closed union key
      bgcolor: statusColor[tone].dot,
      // eslint-disable-next-line security/detect-object-injection -- closed union key
      ...(pulsing ? { animation: `${pulse(statusColor[tone].dot)} 2s ease-out infinite` } : {}),
    }}
  />
)

/** Status pill: dot + label. */
export const StatusPill: FC<{ tone: StatusTone; children: ReactNode; pulse?: boolean }> = ({ tone, children, pulse: pulsing }) => (
  <Box component="span" sx={pillSx(tone)}>
    <StatusDot tone={tone} pulse={pulsing} />
    {children}
  </Box>
)
